import { GreeniePilot, LsoGrade, Trap } from "../types/greenieBoard";

const ICON_PATH = "M12 2c-.218 0 -.432 .002 -.642 .005l-.616 .017l-.299 .013l-.579 .034l-.553 .046c-4.785 .464 -6.732 2.411 -7.196 7.196l-.046 .553l-.034 .579c-.005 .098 -.01 .198 -.013 .299l-.017 .616l-.004 .318l-.001 .324c0 .218 .002 .432 .005 .642l.017 .616l.013 .299l.034 .579l.046 .553c.464 4.785 2.411 6.732 7.196 7.196l.553 .046l.579 .034c.098 .005 .198 .01 .299 .013l.616 .017l.642 .005l.642 -.005l.616 -.017l.299 -.013l.579 -.034l.553 -.046c4.785 -.464 6.732 -2.411 7.196 -7.196l.046 -.553l.034 -.579c.005 -.098 .01 -.198 .013 -.299l.017 -.616l.005 -.642l-.005 -.642l-.017 -.616l-.013 -.299l-.034 -.579l-.046 -.553c-.464 -4.785 -2.411 -6.732 -7.196 -7.196l-.553 -.046l-.579 -.034a28.058 28.058 0 0 0 -.299 -.013l-.616 -.017l-.318 -.004l-.324 -.001z";

const GRADE_CONFIG: Record<LsoGrade, { label: string; color: string; textColor: string }> = {
  "_OK_": { label: "_OK_", color: "#22c55e", textColor: "#dcfce7" },
  "OK": { label: "OK", color: "#166534", textColor: "#bbf7d0" },
  "(OK)": { label: "(OK)", color: "#166534", textColor: "#bbf7d0" },
  "--": { label: "-", color: "#334155", textColor: "#94a3b8" },
  "B": { label: "B", color: "#64748b", textColor: "#94a3b8" },
  "WO": { label: "WO", color: "#64748b", textColor: "#94a3b8" },
  "C": { label: "C", color: "#b52929", textColor: "#fecaca" },
};

function encodeHashSegment(value: string): string {
  return encodeURIComponent(value);
}

export function TrapTile({ color, night, size, maskId }: { color: string; night: boolean; size: number; maskId: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
      <defs>
        <mask id={maskId}>
          <path d={ICON_PATH} fill="white" />
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <rect width="24" height="24" fill={color} />
        {night && <polygon points="24,0 24,24 0,24" fill="#000" />}
      </g>
    </svg>
  );
}

function TrapCell({ trap, maskId }: { trap: Trap | null; maskId: string }) {
  if (!trap) return <div className="gtrap gtrap-empty" />;

  const config = GRADE_CONFIG[trap.grade] ?? { label: trap.grade, color: "#334155", textColor: "#94a3b8" };

  return (
    <div className="gtrap" title={trap.grading_raw ?? trap.grade}>
      <div className="gtrap-tile">
        <TrapTile color={config.color} night={!!trap.night} size={36} maskId={maskId} />
        {(trap.grade === "WO" || trap.grade === "B") && (
          <span className="gtrap-label" style={{ color: config.textColor }}>{config.label}</span>
        )}
        {trap.wire != null && <span className="gtrap-wire">{trap.wire}</span>}
      </div>
    </div>
  );
}

interface GreenieBoardTableProps {
  pilots: GreeniePilot[];
  trapLimit: number;
}

export default function GreenieBoardTable({ pilots, trapLimit }: GreenieBoardTableProps) {
  return (
    <div className="gboard-scroll">
      <table className="gboard-table">
        <colgroup>
          <col style={{ width: "160px" }} />
          {Array.from({ length: trapLimit }, (_, i) => <col key={i} style={{ width: "34px" }} />)}
        </colgroup>
        <tbody>
          {pilots.map((pilot, pi) => {
            const cells: (Trap | null)[] = Array.from({ length: trapLimit }, (_, i) => pilot.traps[i] ?? null);
            return (
              <tr key={pilot.ucid} className="gboard-row">
                <td className="gboard-name">
                  <a className="gboard-name-link" href={`#/pilots/${encodeHashSegment(pilot.ucid)}`}>
                    {pilot.name}
                  </a>
                </td>
                {cells.map((trap, i) => (
                  <td key={i} className="gboard-cell">
                    <TrapCell trap={trap} maskId={`tm-${pilot.ucid}-${pi}-${i}`} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
